import { useState, useEffect } from "react";
import { Breadcrumb } from "react-bootstrap";
import { useSelector } from "react-redux";
import { NavLink, useHistory } from "react-router-dom";

import bankAccountApi from "../../api/bankAccount";
import { useProfileListByType, UserProfile } from "../../api/profile";
import { Loader, toast } from "../../common/components/core";
import { currentRolesSelector } from "../../redux/slices/auth/selectors";
import { FormSubmissionHandler } from "../../types/common";
import { PageTitle } from "../../types/pageTitle";
import { SuperAdminRole, superAdminAccountType } from "../../types/superadmin";

import AccountForm, { AccountFormValues } from "./components/AccountForm";
import { isNodeDeveloper } from "../../utils/role";
export enum ProfileType {
    ALL = "All_PROFILES",
    DEVELOPER = "DEVELOPER",
    APPROVER = "APPROVER",
    HSBC = "HSBC",
    MAIN_CONTRACTOR = "MAIN_CONTRACTOR",
}

const CreateAccountView = (): JSX.Element => {
    document.title = PageTitle.CreateAccountView;
    const history = useHistory();
    const currentRoles = useSelector(currentRolesSelector);
    const accountType = superAdminAccountType(currentRoles);
    const isDeveloper = isNodeDeveloper();
    const currentProListType = () => {
        if(isDeveloper) {
            return ProfileType.DEVELOPER;
        } else {
            return ProfileType.MAIN_CONTRACTOR;
        }
    }
    const { data: profileNameList = [] } = useProfileListByType(currentProListType());
    const [isSubmitting, setSubmitting] = useState(false);

    useEffect(() => {
        return () => {
            setSubmitting(false);
        };
    }, []);
    const handleSubmit: FormSubmissionHandler<AccountFormValues> = async (
        values,
        { setErrors },
    ) => {
        if (!values) {
            return;
        }

        setSubmitting(true);
        try {
            const bankAccountCreatedRes = await bankAccountApi.create(values);
            history.push(
                `/account-setup/bank-account-details/${bankAccountCreatedRes.id}`,
            );
            toast("Bank Account Created");
        } catch (e) {
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Loader isLoading={isSubmitting}>
            {accountType && (
                <PureCreateAccountView
                    profileNameList={profileNameList}
                    accountType={accountType}
                    currencies={["SGD", "USD", "CNY"]}
                    onSubmit={handleSubmit}
                    onCancel={() => history.push("/account-setup")}
                />
            )}
        </Loader>
    );
};

type PureCreateAccountViewProps = {
    accountType: SuperAdminRole;
    currencies: string[];
    onSubmit: FormSubmissionHandler<AccountFormValues>;
    onCancel: () => void;
    profileNameList: UserProfile[];
};

export const PureCreateAccountView = ({
    accountType,
    currencies,
    onSubmit,
    onCancel,
    profileNameList,
}: PureCreateAccountViewProps): JSX.Element => {
    return (
        <>
            <Breadcrumb className="mb-5">
                <Breadcrumb.Item linkAs="span">
                    <NavLink to="/account-setup">{`${accountType} Account Setup`}</NavLink>
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="fw-bold">
                    {`Add ${accountType} Bank Account`}
                </Breadcrumb.Item>
            </Breadcrumb>

            <AccountForm
                profileNameList={profileNameList}
                accountType={accountType}
                currencies={currencies}
                onSubmit={onSubmit}
                onCancel={onCancel}
                initialValues={{
                    holderName: "",
                    accountNumber: "",
                    swiftAddress: "",
                    bankName: "",
                    registeredAddress: "",
                    city: "Singapore",
                    countryCode: "SG",
                    paymentCurrency: "",
                    profile: "",
                }}
            />
        </>
    );
};

export default CreateAccountView;
